import { useState, useEffect } from 'react'
import { router, usePage } from '@inertiajs/react'
import PublicLayout from '../../components/PublicLayout'
import Button from '../../components/Button'

function formatCurrency(amount) {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount || 0)
}

function ImageCarousel({ images, onOpenLightbox }) {
  const [index, setIndex] = useState(0)

  return (
    <div className="relative w-[120px] h-[120px] flex-shrink-0 bg-gray-100">
      <img
        src={images[index].url}
        alt=""
        className="w-full h-full object-cover cursor-zoom-in"
        onClick={() => onOpenLightbox(index)}
      />
      {images.length > 1 && (
        <>
          {index > 0 && (
            <button
              onClick={() => setIndex(i => i - 1)}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
            >
              ‹
            </button>
          )}
          {index < images.length - 1 && (
            <button
              onClick={() => setIndex(i => i + 1)}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
            >
              ›
            </button>
          )}
          <div className="absolute bottom-1.5 left-0 right-0 flex justify-center gap-1">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function Lightbox({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex)

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setIndex(i => Math.max(0, i - 1))
      if (e.key === 'ArrowRight') setIndex(i => Math.min(images.length - 1, i + 1))
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, images.length])

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <img src={images[index].url} alt="" className="w-full max-h-[80vh] object-contain" />
        <button
          onClick={onClose}
          className="absolute -top-9 right-0 text-white/70 hover:text-white text-3xl leading-none"
        >
          ×
        </button>
        {index > 0 && (
          <button
            onClick={() => setIndex(i => i - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center text-2xl hover:bg-black/70"
          >
            ‹
          </button>
        )}
        {index < images.length - 1 && (
          <button
            onClick={() => setIndex(i => i + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center text-2xl hover:bg-black/70"
          >
            ›
          </button>
        )}
        {images.length > 1 && (
          <p className="absolute -bottom-7 left-0 right-0 text-center text-sm text-white/50">
            {index + 1} / {images.length}
          </p>
        )}
      </div>
    </div>
  )
}

function ProductCard({ product, quantity, onAdd, onRemove }) {
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const images = product.images || []

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex">
        {images.length > 0 && (
          <ImageCarousel images={images} onOpenLightbox={setLightboxIndex} />
        )}
        <div className="p-4 flex items-start gap-4 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900">{product.name}</p>
            {product.description && (
              <p className="text-sm text-gray-500 mt-0.5">{product.description}</p>
            )}
            <p className="text-lg font-semibold text-gray-900 mt-2">{formatCurrency(product.price)}</p>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-shrink-0">
            {quantity > 0 ? (
              <>
                <button
                  onClick={onRemove}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-lg hover:bg-gray-50"
                >
                  −
                </button>
                <span className="w-6 text-center font-medium">{quantity}</span>
                <button
                  onClick={onAdd}
                  className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg hover:bg-indigo-700"
                >
                  +
                </button>
              </>
            ) : (
              <button
                onClick={onAdd}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Lägg till
              </button>
            )}
          </div>
        </div>
      </div>
      {lightboxIndex !== null && (
        <Lightbox images={images} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </>
  )
}

function CheckoutForm({ cart, products, storeToken, onBack }) {
  const { props } = usePage()
  const errors = props.errors || {}

  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const items = Object.entries(cart)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => ({ product_id: parseInt(id), quantity: qty }))

  const total = items.reduce((sum, item) => {
    const product = products.find(p => p.id === item.product_id)
    return sum + (product?.price || 0) * item.quantity
  }, 0)

  function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    router.post(`/stores/${storeToken}/orders`, {
      order: {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        items: JSON.stringify(items),
      }
    }, {
      onFinish: () => setSubmitting(false)
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="text-sm text-indigo-600 hover:underline">
          ← Tillbaka till produkter
        </button>
        <h2 className="text-xl font-bold text-gray-900 mt-2">Din beställning</h2>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {items.map(item => {
          const product = products.find(p => p.id === item.product_id)
          return (
            <div key={item.product_id} className="flex justify-between px-4 py-3">
              <span className="text-gray-900">{product?.name} × {item.quantity}</span>
              <span className="font-medium">{formatCurrency((product?.price || 0) * item.quantity)}</span>
            </div>
          )
        })}
        <div className="flex justify-between px-4 py-3 font-bold text-gray-900">
          <span>Totalt</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Dina uppgifter</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fullständigt namn</label>
          <input
            type="text"
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {errors['customer_name'] && <p className="mt-1 text-sm text-red-600">{errors['customer_name']}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">E-post</label>
          <input
            type="email"
            value={customerEmail}
            onChange={e => setCustomerEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {errors['customer_email'] && <p className="mt-1 text-sm text-red-600">{errors['customer_email']}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telefonnummer</label>
          <input
            type="tel"
            value={customerPhone}
            onChange={e => setCustomerPhone(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {errors['customer_phone'] && <p className="mt-1 text-sm text-red-600">{errors['customer_phone']}</p>}
        </div>

        <p className="text-xs text-gray-500">Antingen e-post eller telefonnummer krävs.</p>

        {errors.base && <p className="text-sm text-red-600">{errors.base}</p>}

        <Button type="submit" disabled={submitting} className="w-full">
          {submitting ? 'Lägger beställning…' : `Lägg beställning (${formatCurrency(total)})`}
        </Button>

        <p className="text-xs text-gray-400 text-center">
          Ingen betalning krävs — detta är ett köpåtagande. Vi återkommer med betalningsuppgifter.
        </p>
      </form>
    </div>
  )
}

export default function StoresShow({ store, products }) {
  const [cart, setCart] = useState({})
  const [showCheckout, setShowCheckout] = useState(false)

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0)

  function addToCart(productId) {
    setCart(c => ({ ...c, [productId]: (c[productId] || 0) + 1 }))
  }

  function removeFromCart(productId) {
    setCart(c => {
      const qty = (c[productId] || 0) - 1
      if (qty <= 0) {
        const { [productId]: _, ...rest } = c
        return rest
      }
      return { ...c, [productId]: qty }
    })
  }

  if (showCheckout) {
    return (
      <PublicLayout title={store.activity_name}>
        <CheckoutForm
          cart={cart}
          products={products}
          storeToken={store.token}
          onBack={() => setShowCheckout(false)}
        />
      </PublicLayout>
    )
  }

  return (
    <PublicLayout title={store.activity_name}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{store.activity_name}</h1>
          <p className="text-gray-500 mt-1">Säljer för {store.seller_name}</p>
        </div>

        {products.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
            <p className="text-gray-500">Inga produkter tillgängliga ännu.</p>
          </div>
        )}

        <div className="grid gap-4">
          {products.map(p => (
            <ProductCard
              key={p.id}
              product={p}
              quantity={cart[p.id] || 0}
              onAdd={() => addToCart(p.id)}
              onRemove={() => removeFromCart(p.id)}
            />
          ))}
        </div>

        {cartCount > 0 && (
          <div className="sticky bottom-4">
            <Button
              onClick={() => setShowCheckout(true)}
              className="w-full shadow-lg"
            >
              Till kassan ({cartCount} artikel{cartCount !== 1 ? 'ar' : ''})
            </Button>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
